import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EventStatusTone, event_status, eventStatusTone } from '../../../../assets/constData/event_status';
import { Router } from '@angular/router';
import { CommonServiceService } from '../../../utilities/services/common-service/common-service.service';
import { OrderHistoryService } from '../../../utilities/services/order-history/order-history.service';
import { MapOptionsDrawerComponent } from './map-options-drawer/map-options-drawer.component';
import { TranslateModule } from '@ngx-translate/core';
import { CapitalizeTitlePipe } from '../../../shared/pipes/capitalize-title.pipe';
import { eventImageUrl, hasImageUrlFallback } from '../../../utilities/event-image-url.util';
@Component({
  selector: 'stupa-tournament-card',
  standalone: true,
  imports: [CommonModule, MapOptionsDrawerComponent,TranslateModule, CapitalizeTitlePipe],
  templateUrl: './tournament-card.component.html',
  styleUrl: './tournament-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentCardComponent implements OnInit, OnChanges {

  constructor(
    private readonly router: Router,
    private readonly commonService: CommonServiceService,
    private readonly orderService: OrderHistoryService,
    private readonly cdr: ChangeDetectorRef,
  ) { }




  @Input() image: string = '';
  @Input() compact = false;
  @Input() status: string = 'Registrations Open';
  @Input() closesIn: any = null;
  @Input() dateRange: string = '';
  @Input() title: string = '';
  @Input() location: string = '';
  @Input() distanceKm: number | null = null;
  @Input() registeredCount: number | string = 0;
  @Input() buttonText: string = 'Register Now';
  @Input() event_id: number = 0;
  @Input() endDate: string | Date = '';
  @Input() eventData: any = {};
  @Input() isBroadcastRoute: boolean = false;
  @Input() priority: boolean = false;
  @Input() rowIndex: number = -1;

  imageLoaded: boolean = false;
  imageError: boolean = false;
  eventStatusTitle: string = '';
  isRegistrationOpen: boolean = false;
  cachedStatusVariant: EventStatusTone = 'upcoming';
  formattedClosesIn: string | null = null;
  @Output() register = new EventEmitter<void>();
  @Output() newRegister = new EventEmitter<void>();

  onNewRegister(event: Event) {
    event.stopPropagation();
    if (globalThis.window?.sessionStorage) {
      this.saveEventDataInSession(this.eventData || {});
    }
    this.newRegister.emit();
  }
  @Output() cardClick = new EventEmitter<void>();
  @Output() viewDetails = new EventEmitter<void>();
  @Output() showLogin = new EventEmitter<void>();
  @Output() viewRegisterCount = new EventEmitter<void>();
  showMapDrawer: boolean = false;

  ngOnInit(): void {
    this.updateStatusAndClosesIn();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['image']) {
      this.imageLoaded = false;
      this.imageError = false;
      this.imageUseOriginal = false;
    }
    if (changes['status'] || changes['closesIn']) {
      this.updateStatusAndClosesIn();
    }
  }

  private updateStatusAndClosesIn(): void {
    this.eventStatusTitle = this.getEventStatus(this.status);
    this.isRegistrationOpen = this.eventStatusTitle === 'REGISTRATION OPEN';
    this.cachedStatusVariant = eventStatusTone(this.status);
    this.formattedClosesIn = this.computeClosesIn();
  }

  /** Set once the rewritten image fails, so the card retries the URL it was given before showing the placeholder. */
  private imageUseOriginal = false;

  get resolvedImageUrl(): string {
    return eventImageUrl(this.image, this.imageUseOriginal);
  }

  get isPriority(): boolean {
    return this.priority || (this.rowIndex >= 0 && this.rowIndex < 3);
  }

  onImageLoad(): void {
    this.imageLoaded = true;
    this.imageError = false;
    this.cdr.markForCheck();
  }

  onImageError(): void {
    this.imageLoaded = false;
    if (!this.imageUseOriginal && hasImageUrlFallback(this.image)) {
      this.imageUseOriginal = true;
    } else {
      this.imageError = true;
    }
    this.cdr.markForCheck();
  }
  onRegister() {
    // Check if user is logged in
    const token = globalThis.window?.localStorage
      ? localStorage.getItem('token')
      : null;
    if (!token && this.getEventStatus(this.status) === 'REGISTRATION OPEN') {
      if (globalThis.window?.sessionStorage) {
        this.saveEventDataInSession(this.eventData || {});
        sessionStorage.setItem('redirectAfterLogin', '/events/1');
      }
      this.commonService.login_subject.next(true);
      this.commonService.is_login_success.next(false);
      this.showLogin.emit();
      return;
    }

    if (this.getEventStatus(this.status) !== 'REGISTRATION OPEN') {
      // Emit viewDetails event so parent can handle player/non-player logic
      this.viewDetails.emit();
      return;
    }
    this.register.emit();
  }

  getEventStatus(status: string): string {
    const statusObj = event_status.find((s) => Number(s.value) === Number(status));
    return statusObj ? statusObj.title : 'Unknown';
  }

  /** Maps the raw status title to a visual badge variant so each status reads at a glance. */
  get statusVariant(): EventStatusTone {
    return this.cachedStatusVariant;
  }

  onViewRegister() {
    // Emit viewDetails event so parent can handle player/non-player logic
    this.viewRegisterCount.emit();
  }

  onCardClick() {
    this.cardClick.emit();
  }

  toggleMapDrawer(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.showMapDrawer = !this.showMapDrawer;
  }

  handleOpenMap(type: 'google' | 'apple') {
    this.showMapDrawer = false;
    if (this.location) {
      let url = '';
      if (type === 'google') {
        url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.location)}`;
      } else {
        // Open Apple Maps (App on iOS/Mac, Web fallback elsewhere)
        url = `https://maps.apple.com/?q=${encodeURIComponent(this.location)}`;
      }
      globalThis.window.open(url, '_blank');
    }
  }

  private saveEventDataInSession(event: any) {
    if (
      globalThis.window?.sessionStorage
    ) {
      sessionStorage.setItem('selectedTournamentName', event.name);
      sessionStorage.setItem(
        'selectedTournamentDetails',
        JSON.stringify(event)
      );
      sessionStorage.setItem('event_id', event.id);
      sessionStorage.setItem('event_type', event.event_type);
      sessionStorage.setItem('delegation', event.delegation);
      // store event in the session storage
      sessionStorage.setItem('event', JSON.stringify(event));
      this.orderService.selectedTournamentName = event.name;
    }
  }
  openGoogleMap(event: MouseEvent) {
    event.stopPropagation();
    if (this.location) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.location)}`;
      globalThis.window.open(url, '_blank');
    }
  }

  get getClosesIn(): string | null {
    return this.formattedClosesIn;
  }

  private computeClosesIn(): string | null {
    if (!this.closesIn) return null;
    
    // Check if hours are 0 or less
    if (this.closesIn.total_hours !== undefined && this.closesIn.total_hours <= 0) return null;
    
    // If no days, show hours (only if hours > 0)
    if (!this.closesIn.total_days) {
      return this.closesIn.total_hours > 0 
        ? `Closes in ${this.closesIn.total_hours} hour${this.closesIn.total_hours === 1 ? '' : 's'}`
        : null;
    }
    
    // Show days (only if days > 0)
    if (this.closesIn.total_days > 0) {
      return `Closes in ${this.closesIn.total_days} day${this.closesIn.total_days === 1 ? '' : 's'}`;
    }

    return null;
  }
}

